/**
 * REL-29 — the deterministic AGE step, at the B26 auth-gate seam.
 *
 * WHY IT EXISTS. Age capture today is incidental: it rides in via chat
 * extraction, which is how five adult accounts reached 2026-08-26 with no age
 * at all. Age is not decoration here — it drives the clinical bands, the 13-15
 * guardian beat, the minor-commerce refusal, and the COPPA hold. A signal that
 * important cannot depend on someone mentioning their birthday to a chatbot.
 *
 * WHERE. Mounted at the seam in `routes/onboarding.tsx`, below the B26 auth
 * guard and above the step-0 fork, exactly as the seam comment specifies. It is
 * an UNNUMBERED `metaOverride` screen (the StepZeroFork / CoachOnboarding /
 * GuardianInviteBeat pattern), so the step machine, `StepNumber`, `clampStep`
 * and the persisted-blob validator are all untouched.
 *
 * WHAT IT WRITES. `answers.birthYear` + `answers.birthMonth` — the SAME two keys
 * Step 3 already sets, which `usePersistOnboarding` maps to `_birth_year` /
 * `_birth_month` / `Age`, which are exactly the keys `clinicalModifiers.parseAge`
 * reads for the coppa gate. Nothing new is invented on the context. Step 3 hides
 * its own birth row once these are present, so nobody is asked twice.
 *
 * ⛔ B196 (founder ruling 2026-07-25, built 2026-09-17, spec v2) — UNDER 13 IS A
 * STOP, NOT A HOLD, AND THE FIELDS ARE HELD LOCAL UNTIL THEY ARE KNOWN SAFE.
 *
 * The two selects below are NOT wired straight to the wizard's `onChange` (the
 * parent's reducer, which is what persists to `localStorage`). They are
 * MIRRORED into local state first, and forwarded to the parent ONLY once the
 * pair is complete AND resolves to 13 or older (B453: and only when Continue
 * is pressed, never on a pick; the under-13 stop is likewise raised by
 * Continue, or at mount for a stale blob). An under-13 pair is NEVER
 * forwarded — the wizard's reducer and its persisted blob never see it, so
 * there is nothing to race against `useOnboardingState`'s own every-change
 * autosave (a file this lane does not own).
 *
 * This also closes a real bug the B196 security review's F3 finding sits on
 * top of: the OLD wiring forwarded every keystroke straight to the parent, so
 * the instant both selects held a value, `onboarding.tsx`'s `ageStepDue` (keyed
 * on `ageAnswered`) went false and this screen UNMOUNTED itself before Continue
 * was ever clicked — the under-13 branch below never ran for a freshly typed
 * answer. `AgeStep.rel29.test.tsx` and `onboarding.ageStop.b196.test.tsx` pin
 * the fix by driving the real route.
 *
 * A pair that arrives ALREADY under 13 via props (a stale/legacy persisted
 * blob hydrated before this screen mounts) is handled the same way: `showStop`
 * is derived on the very first render, and `onUnder13Detected` — wired in
 * `onboarding.tsx` — clears both the persisted blob and the wizard reducer's
 * in-memory copy. `useOnboardingState`'s autosave effect explicitly skips its
 * own first firing on mount, so there is no competing re-write to race there
 * either. Once shown, the stop is STICKY: it is driven by this component's own
 * local state, not by the parent's props, so clearing those props afterward
 * (deliberately, to close the loop above) can never flip the screen back to
 * the picker underneath the athlete.
 *
 * THE THREE CONTROLS on the stop are "I picked the wrong year" (below),
 * "Remove this sign-in" (below, `onRemoveUnder13`) and sign-out — the last of
 * these is `OnboardingShell`'s own existing "Sign out" link (B6, present in
 * its chrome on every onboarding screen, this one included), not a second
 * control duplicated here. `onUnder13Detected` has already cleared the blob
 * and the in-memory answers by the time any sign-out — the shell's or
 * anyone else's — can run, so the shell's existing control is already safe
 * for this screen; see the lane report for why a second, AgeStep-owned
 * "Sign out" button was cut rather than shipped alongside it.
 *
 * L9 (critic finding 1 / UX review finding 1) — ORDER AND WEIGHT ARE NOT
 * DECORATION. "I picked the wrong year" renders FIRST and styled as the
 * primary control (`.holdPrimary`); "Remove this sign-in" renders LAST,
 * visually secondary (`.holdSecondary`), and asks ONCE before it acts — the
 * first tap arms a Cancel / "Remove this sign-in" confirm pair
 * (`.holdConfirm`), the same idle -> armed posture
 * `AccountSection`'s delete-account control uses; only the second tap calls
 * `onRemoveUnder13`. Both controls are >=44px tall (`AgeStep.module.css`,
 * pinned by `AgeStep.css.b196.test.ts`).
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { StepCta, StepHeading, StepSub } from '../OnboardingShell/StepLayout';
import { BarNote, useOnboardingV2 } from '../OnboardingShell/onboardingV2';
import { AGE_STEP_COPY, UNDER_13_COPY, isUnder13 } from '../../lib/onboardingAgeCopy';
import styles from './AgeStep.module.css';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// B223 — visually-hidden, inline rather than in `AgeStep.module.css`: that
// module is outside this lane's ownership (same posture as the B215 comment
// on the shell's disabled Sign out style, in `OnboardingShell.tsx`).
const srOnlyStyle: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export interface AgeStepProps {
  /** Current answers, so a returning athlete sees their own values. Read
   *  ONLY at mount (see file header) — a later change to these props never
   *  moves this component off the stop once it is showing one. */
  birthYear?: number;
  birthMonth?: number;
  /** Writes straight through to the wizard's answer bag. Called ONLY for a
   *  complete pair that is 13 or older (see file header) — never for an
   *  incomplete pair and never for one under 13. */
  onChange: (patch: { birthYear?: number; birthMonth?: number }) => void;
  /** Both answered and 13+: on to the wizard. */
  onContinue: () => void;
  /** Fires exactly once, the moment a complete pair resolves under 13 —
   *  whether typed just now or inherited via props at mount. The caller
   *  clears the persisted blob and the reducer's own copy of the two birth
   *  keys (B196 F3); this component's own display does not depend on either
   *  afterward. */
  onUnder13Detected: () => void;
  /** The stop screen's "Remove this sign-in" control (B196 F4). Sign-out on
   *  the stop is `OnboardingShell`'s own existing control — see file header. */
  onRemoveUnder13: () => void;
  /** B211 — the sentence for the MOST RECENT failed removal, `null`/
   *  `undefined` for none (the common case, and the moment right after a
   *  successful removal, which unmounts this screen into sign-out before it
   *  matters). The caller (`onboarding.tsx`'s `onRemoveUnder13` handler)
   *  computes this from `removeRefusedSignup()`'s result via
   *  `refusedSignupRemovalMessage` (`lib/refusedAccount.ts`) — this
   *  component renders it verbatim and never maps a code itself, so the two
   *  server sentences and the one bounded 429 sentence live in exactly one
   *  place. A CHANGE in this value (not merely its presence, so a second
   *  failure with the SAME sentence still disarms) drops the confirm pair
   *  back to the un-armed secondary button — a failed removal is never left
   *  showing Cancel/confirm as if nothing happened. */
  removeError?: string | null;
  /** B215 — fired whenever the derived `removalInFlight` value changes, so
   *  the shell's Sign out control can be gated the same way the stop's own
   *  three controls already are (a removal must not be abandoned mid-flight
   *  by signing out). The route holds the latest value in state and passes
   *  it down to `OnboardingShell`; this component owns the fact, never a
   *  context or a global. Optional — every other consumer omits it and sees
   *  no behaviour change. */
  onRemovalInFlightChange?: (inFlight: boolean) => void;
  /** Injectable for tests; the boundary is a date, so it must be pinnable. */
  now?: Date;
}

export function AgeStep({
  birthYear,
  birthMonth,
  onChange,
  onContinue,
  onUnder13Detected,
  onRemoveUnder13,
  removeError,
  onRemovalInFlightChange,
  now,
}: AgeStepProps) {
  // Part 7 (flag `onboarding_2_0_v1`): the reason rides in the docked bar, and the stop's
  // heading is a plain statement in DM Sans.
  const v2 = useOnboardingV2();
  /**
   * B196, founder ruling `floor: neutral` (2026-09-20): the year list stays
   * `thisYear - 5` down to `thisYear - 99`, the same window `Step3ParQ.tsx`
   * offers. The age question is NEUTRAL on purpose: an under-13 can state a
   * true year and meets the stop below, instead of a list that only offers
   * 13-plus years and so hints at the answer (the spec's row 21 floor of 13
   * was superseded after the founder walk on 2026-09-20).
   */
  const years = (() => {
    const thisYear = (now ?? new Date()).getFullYear();
    const out: number[] = [];
    for (let y = thisYear - 5; y >= thisYear - 99; y--) out.push(y);
    return out;
  })();

  // Local mirror of the two fields — see the file header for why these are
  // NOT the wizard's own `state.answers`. Seeded once from props so a pair
  // already known (an in-progress 13+ answer, or a stale under-13 blob) shows
  // up without having to be re-picked.
  //
  // UX review finding 2 (2026-09-21) — the same B123 guard `Step3ParQ.tsx`
  // applies to a hydrated blob: `birthMonth`/`birthYear` come from
  // `loadPersistedStateForUser`'s bare `as OnboardingAnswers` cast, so a
  // malformed value (a numeric string, an old/hand-edited blob) is possible
  // at runtime despite the `number | undefined` prop type. `<select>`
  // stringifies for its DOM comparison, so an un-guarded seed can still
  // visually select the matching option while `complete` (`typeof ===
  // 'number'`) reads false — a phantom pick with Continue disabled and no
  // visible reason. Reading only a genuine `number` keeps the shown value
  // and the completeness gate in lockstep: a non-number is absent for both.
  const [localMonth, setLocalMonth] = useState<number | undefined>(
    typeof birthMonth === 'number' ? birthMonth : undefined,
  );
  const [localYear, setLocalYear] = useState<number | undefined>(
    typeof birthYear === 'number' ? birthYear : undefined,
  );
  const complete = typeof localMonth === 'number' && typeof localYear === 'number';
  const under13 = complete && isUnder13(localYear!, localMonth!, now);

  // B453 (walk 2026-10-22) — THE PAIR IS COMMITTED ON CONTINUE, NEVER ON A
  // PICK. The stop is raised by pressing Continue with an under-13 pair (or by
  // a pair that arrives under 13 via props at mount, the stale-blob case), not
  // by the second select changing: a keyboard or screen-reader adult stepping
  // down the year list passes 2021 first and must not meet the stop on the way
  // to their own year. "I picked the wrong year" is the only way back to the
  // picker once the stop has shown, and any further edit to either field
  // retires it, so a NEW under-13 pick meets the stop again only on Continue.
  const [stopRaised, setStopRaised] = useState(
    () =>
      typeof birthMonth === 'number' &&
      typeof birthYear === 'number' &&
      isUnder13(birthYear, birthMonth, now),
  );
  const showStop = under13 && stopRaised;

  // B196 L9 (critic finding 1 / UX review finding 1) — "Remove this sign-in"
  // asks ONCE before it acts: the first tap arms a Cancel/confirm pair (the
  // same posture `AccountSection`'s delete-account control uses — idle ->
  // armed, never a one-tap irreversible action), the second tap fires
  // `onRemoveUnder13`. No sentence beyond the ratified `UNDER_13_COPY` set is
  // introduced; "Cancel" is the same word the deletion flow's own confirm
  // reuses, not a new one. Cleared whenever the stop itself is dismissed, so
  // a re-triggered stop never opens already armed.
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  // B213 — the confirm pair disables the instant the confirm tap fires the
  // removal, so an impatient extra tap (the ledger row: three POSTs in one
  // second on the branch preview) cannot send a second request for the same
  // subject and race the shared deletion executor. Success unmounts this
  // screen (sign-out), so this needs a release only on failure, which the
  // existing `removeError` effect below already provides.
  const [removing, setRemoving] = useState(false);

  const notifiedRef = useRef(false);
  useEffect(() => {
    if (showStop && !notifiedRef.current) {
      notifiedRef.current = true;
      onUnder13Detected();
    }
  }, [showStop, onUnder13Detected]);

  // B223 — "I picked the wrong year" moves focus to the year select, and the
  // return is announced. `OnboardingShell`'s own heading-focus effect
  // (`OnboardingShell.tsx:157-160`) is keyed on `current`/`bare`/`liveMessage`,
  // none of which change on this internal stop-to-picker transition (this
  // screen is one `metaOverride` mount throughout), so it never fires here —
  // this step moves focus itself. Keyed on the stop LEAVING (`showStop` true
  // -> false), so it runs the render after the picker's selects are back in
  // the DOM and the ref below is populated; a plain mount with `showStop`
  // already false (the ordinary case) does not steal focus.
  const yearSelectRef = useRef<HTMLSelectElement | null>(null);
  const wasShowingStopRef = useRef(showStop);
  // The sentence read through the visually-hidden status span in the picker
  // view below, once Back has fired — reuses `AGE_STEP_COPY.sub`, already on
  // screen there, so no new string is introduced.
  //
  // UX review finding 1 (2026-09-21) — `announceReturn` (armed by the Back
  // button) only decides whether the span exists at all; the TEXT is a
  // SEPARATE piece of state, `announceText`, set HERE in the same effect
  // that moves focus — one commit after the transition, never together
  // with it. A live region inserted with its first text already inside is
  // not announced by most screen readers (`Step3ParQ.tsx`'s ".count"
  // region documents the same rule): the mount (`announceReturn`, set in
  // the same batch as the Back tap that flips `showStop`) and the text
  // (`announceText`, set a commit later, right here) are kept on two
  // different state updates, so the span's first paint is empty and it
  // only receives its sentence on the mutation that follows.
  const [announceReturn, setAnnounceReturn] = useState(false);
  const [announceText, setAnnounceText] = useState('');
  useEffect(() => {
    if (wasShowingStopRef.current && !showStop) {
      yearSelectRef.current?.focus();
      setAnnounceText(AGE_STEP_COPY.sub);
    }
    wasShowingStopRef.current = showStop;
  }, [showStop]);

  // B211 — a failed removal (403, 429, anything) disarms the confirm pair
  // rather than leaving Cancel/confirm on screen as if the tap did nothing.
  // Keyed on the VALUE, not merely truthiness, so a second failure carrying
  // the same sentence still disarms (the athlete tapped confirm again).
  useEffect(() => {
    if (removeError) {
      setConfirmingRemove(false);
      setRemoving(false);
    }
  }, [removeError]);

  // B213 — derived, not read from `removing` alone. `useEffect` above is a
  // PASSIVE effect: it resets `removing` a render AFTER `removeError` first
  // turns truthy, never in the SAME commit. Gating the disabled attribute on
  // `removing` alone left a one-tick window, immediately after a failure,
  // where the confirm pair was rendered disabled beside its own error
  // sentence — real in a fast, scripted double-tap. Folding `!removeError`
  // into the SAME expression that drives `disabled` closes the window: the
  // instant the failure's sentence is on screen, the controls are live again,
  // in that very render, with no dependency on the effect having run yet.
  const removalInFlight = removing && !removeError;

  // B215 — the shell's Sign out control is gated by this same value, reached
  // through the route (see the prop doc above). Reported from an effect, not
  // read straight off `removalInFlight` by the caller, so the parent never
  // has to re-derive the removing/removeError pair itself.
  useEffect(() => {
    onRemovalInFlightChange?.(removalInFlight);
  }, [removalInFlight, onRemovalInFlightChange]);

  // B453 — a pick changes the LOCAL pair only. Nothing reaches the parent (and
  // so nothing reaches the reducer or its persisted blob) until `submit`, which
  // is what keeps this screen mounted until the athlete presses Continue.
  const pick = (nextMonth: number | undefined, nextYear: number | undefined) => {
    setLocalMonth(nextMonth);
    setLocalYear(nextYear);
    setStopRaised(false);
    setConfirmingRemove(false);
  };

  if (showStop) {
    return (
      <>
        {v2 ? (
          <h2 className={styles.holdTitle}>{UNDER_13_COPY.heading}</h2>
        ) : (
          <StepHeading>{UNDER_13_COPY.heading}</StepHeading>
        )}
        <p className={styles.holdBody}>{UNDER_13_COPY.body}</p>
        {/* The way back is FIRST and PRIMARY (critic finding 1 / UX review
            finding 1) — the easy, reversible fix for the most likely real
            trigger of this screen (a mis-tapped birth year) is the first
            and most prominent control, not the irreversible one. */}
        <button
          type="button"
          className={styles.holdPrimary}
          onClick={() => {
            // B213 (critic finding 1) — Back is gated like the pair below: a
            // tap while the removal is in flight must not dismiss the stop
            // under a deletion that keeps running and signs the user out.
            if (removalInFlight) return;
            // B221 — clears the YEAR only; the month stays picked. The pair
            // is now incomplete, so `under13` is already false and `showStop`
            // is false on that alone; `setStopRaised(false)` is kept anyway as
            // the single source of truth `showStop` reads (belt on top of
            // the completeness gate, the same defensive posture the B213
            // guards above use), not because it is load-bearing here.
            setLocalYear(undefined);
            setStopRaised(false);
            setConfirmingRemove(false);
            // B223 — arms the return announcement rendered in the picker
            // view below; the focus move itself is the effect above, keyed
            // on `showStop` leaving.
            setAnnounceReturn(true);
          }}
          disabled={removalInFlight}
        >
          {UNDER_13_COPY.back}
        </button>
        {/* "Remove this sign-in" is LAST, visually secondary, and asks ONCE
            before it acts — the same idle -> armed posture
            `AccountSection`'s delete-account control uses, reused rather
            than invented. */}
        {confirmingRemove ? (
          <div className={styles.holdConfirm}>
            <button
              type="button"
              className={styles.holdCancel}
              onClick={() => setConfirmingRemove(false)}
              disabled={removalInFlight}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.holdRemoveConfirm}
              onClick={() => {
                // B213 — the guard itself: a tap while already removing does
                // nothing (belt on top of the disabled attribute below, which
                // a real browser already refuses to click at all).
                if (removalInFlight) return;
                setRemoving(true);
                onRemoveUnder13();
              }}
              disabled={removalInFlight}
              aria-busy={removalInFlight}
            >
              {UNDER_13_COPY.remove}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={styles.holdSecondary}
            onClick={() => setConfirmingRemove(true)}
          >
            {UNDER_13_COPY.remove}
          </button>
        )}
        {/* B211 — a refused removal tells the athlete why, under the stop's
            own controls, never a silently-swallowed failure. */}
        {removeError && <p className={styles.holdRemoveError}>{removeError}</p>}
      </>
    );
  }

  const submit = () => {
    if (!complete) return;
    if (under13) {
      // B453 — an under-13 pair meets the stop HERE, on Continue, with
      // nothing forwarded and no forward path.
      setStopRaised(true);
      return;
    }
    // B453 — the commit. A complete 13+ pair is forwarded to the wizard only
    // now, and `onContinue` follows in the same event so the step hands off
    // exactly once, on the press.
    onChange({ birthMonth: localMonth, birthYear: localYear });
    onContinue();
  };

  return (
    <>
      <StepHeading>{AGE_STEP_COPY.heading}</StepHeading>
      <StepSub>{AGE_STEP_COPY.sub}</StepSub>
      {/* B223 — announces the return from the stop through a visually-hidden
          status span, reusing `AGE_STEP_COPY.sub` (no new string). Mounted
          only after Back has fired (see the effect above): an ordinary first
          visit to this picker never renders it, so nothing is announced that
          was not there before.
          UX review finding 1 (2026-09-21) — MOUNTED EMPTY, filled a commit
          later. `announceReturn` gates the span's existence (armed in the
          same batch the Back tap flips this view in); `announceText` is a
          separate piece of state, set only by the focus effect above, one
          commit after that mount. The span therefore reaches the DOM empty
          and receives its sentence as a later mutation, not together with
          its own insertion — a live region inserted pre-filled is not
          announced by most screen readers. */}
      {announceReturn && (
        <span role="status" style={srOnlyStyle}>
          {announceText}
        </span>
      )}

      <div className={styles.selects} role="group" aria-label="Birth month and year">
        <select
          className={styles.select}
          aria-label={AGE_STEP_COPY.monthLabel}
          value={localMonth ?? ''}
          onChange={(e) =>
            pick(e.target.value ? Number(e.target.value) : undefined, localYear)
          }
        >
          <option value="">{AGE_STEP_COPY.monthPlaceholder}</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </select>
        <select
          ref={yearSelectRef}
          className={styles.select}
          aria-label={AGE_STEP_COPY.yearLabel}
          value={localYear ?? ''}
          onChange={(e) =>
            pick(localMonth, e.target.value ? Number(e.target.value) : undefined)
          }
        >
          <option value="">{AGE_STEP_COPY.yearPlaceholder}</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      <StepCta onClick={submit} disabled={!complete}>
        {AGE_STEP_COPY.cta}
      </StepCta>
      {/* Fix round 1 (critic LOW 2) — under the docked bar (Part 7) the line stays mounted
          and only stops saying anything, with its room named in `reserve`, so completing
          the pair moves nothing. Flag off it is unmounted when complete, as it always was. */}
      {(v2 || !complete) && (
        <BarNote className={styles.hint} reserve={[AGE_STEP_COPY.incomplete]}>
          {complete ? null : AGE_STEP_COPY.incomplete}
        </BarNote>
      )}
    </>
  );
}

export default AgeStep;
